import React, { useEffect, useMemo } from "react";
import ClassStudentPicker from "../EOYReportAssistant/ClassStudentPicker";
import PTMReportCard from "./PTMReportCard";
import useEnhancedReportWorkflow from "./useEnhancedReportWorkflow";
import { getStudentDisplayName } from "./enhancedReportsUtils";
import {
  generateBatchPTMReports,
  getPTMClassReports,
} from "../../services/APIClient";

/**
 * Parent Teacher Meeting tab. Same shared-selection + per-tab workflow
 * pattern as the other two tabs; the only report-specific control is the
 * academic year. `academicYear` is owned by EnhancedAIReportsPanel (not
 * this component) because it's also fed into the shared class-selection
 * hook so the class list matches the selected year.
 */
function PTMTab({
  selection,
  yearOptions,
  academicYear,
  onAcademicYearChange,
  title,
  subheading,
}) {
  const extraFields = useMemo(() => ({ academicYear }), [academicYear]);
  const extraFieldsKey = academicYear;

  const buildStudentPayload = (studentId) => ({ studentId, academicYear });

  const workflow = useEnhancedReportWorkflow({
    generateBatch: generateBatchPTMReports,
    getClassReports: getPTMClassReports,
    buildStudentPayload,
    extraFields,
  });

  useEffect(() => {
    workflow.resetReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.selectedClassId, extraFieldsKey]);

  useEffect(() => {
    if (!selection.selectedClassId || selection.studentsLoading) return;
    const ids = selection.students.map((student) => student.id).filter(Boolean);
    if (ids.length > 0) workflow.loadSavedReports(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selection.selectedClassId,
    selection.studentsLoading,
    selection.students,
    extraFieldsKey,
  ]);

  const handleGenerate = () => {
    if (!selection.disclaimerAccepted) return;

    const selectedStudents = selection.selectedStudentIds
      .filter((id) => !workflow.doneStudentIds.includes(id))
      .map((id) => {
        const student = selection.students.find((s) => s.id === id);
        return { student_id: id, display_name: getStudentDisplayName(student, id) };
      });

    if (selectedStudents.length === 0) return;
    workflow.generate(selectedStudents);
  };

  return (
    <div className="eoy-panel-root">
      <div className="eoy-header">
        <h1 className="eoy-title">{title}</h1>
        <p className="eoy-subtitle">{subheading}</p>
      </div>

      <div className="eoy-picker">
        <div className="eoy-field">
          <label className="eoy-field-label" htmlFor="ptm-academic-year">
            Academic year
          </label>
          <select
            id="ptm-academic-year"
            className="eoy-select"
            value={academicYear}
            onChange={(e) => onAcademicYearChange(e.target.value)}
            disabled={workflow.generating}
          >
            {yearOptions.map((year) => (
              <option key={year.storage} value={year.storage}>
                {year.display}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ClassStudentPicker
        classes={selection.filteredClasses}
        classesLoading={selection.classesLoading}
        classesError={selection.classesError}
        selectedGradeLevel={selection.selectedGradeLevel}
        onGradeLevelChange={selection.handleGradeLevelChange}
        selectedClassId={selection.selectedClassId}
        onClassChange={selection.handleClassChange}
        students={selection.students}
        studentsLoading={selection.studentsLoading}
        studentsError={selection.studentsError}
        selectedStudentIds={selection.selectedStudentIds}
        unavailableStudentIds={workflow.doneStudentIds}
        onStudentToggle={(id) =>
          selection.handleStudentToggle(id, workflow.doneStudentIds)
        }
        onSelectAll={() => selection.handleSelectAll(workflow.doneStudentIds)}
        disclaimerAccepted={selection.disclaimerAccepted}
        onDisclaimerAcceptedChange={selection.setDisclaimerAccepted}
        onGenerate={handleGenerate}
        generating={workflow.generating || workflow.savedReportsLoading}
      />

      {workflow.savedReportsLoading && selection.selectedClassId && (
        <div className="eoy-muted eoy-saved-reports-status">
          Checking for saved reports...
        </div>
      )}

      {workflow.generating && workflow.progress.total > 0 && (
        <div className="eoy-progress-meta">
          Generating report{" "}
          {Math.min(workflow.progress.done + 1, workflow.progress.total)} of{" "}
          {workflow.progress.total}...
        </div>
      )}

      {!workflow.generating && workflow.reports.length > 0 && (
        <div className="eoy-done-summary">
          <strong>
            {workflow.reportsSource === "saved"
              ? `${workflow.reports.length} saved report${
                  workflow.reports.length === 1 ? "" : "s"
                } loaded`
              : `${workflow.reports.filter((r) => r.success).length} of ${
                  workflow.reports.length
                } reports generated`}
          </strong>
          {selection.selectedClassName && <span> for {selection.selectedClassName}</span>}
        </div>
      )}

      {workflow.genError && <div className="eoy-error">{workflow.genError}</div>}

      {workflow.reports.map((report) => (
        <PTMReportCard
          key={report.student_id}
          report={report}
          className={selection.selectedClassName}
          academicYear={academicYear}
          onRegenerateReport={workflow.regenerate}
          regenerating={workflow.generating}
        />
      ))}
    </div>
  );
}

export default PTMTab;
